import React, { useState, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Filter,
  Plus,
  Calendar,
  CreditCard,
  Percent,
  Tag,
  Users,
  Building,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import SchemeDetailModal from './SchemeDetailModal';
import AddSchemeForm from './AddSchemeForm';
import { Scheme } from '../../services/scheme';
import {
  fetchSchemesThunk,
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setFilters,
  resetFilters,
} from '../../features/schemes/schemeSlice';
import { validateCustomerSearchInput } from "../../utils/searchValidator";


const SchemesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    schemes,
    total,
    loading,
    page,
    pageSize,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    hasFetched,
  } = useAppSelector((state) => state.scheme);
  const { token } = useAppSelector((state) => state.auth);

  const [selectedScheme, setSelectedScheme] = useState<Scheme | null>(null);
  const [schemeDetailOpen, setSchemeDetailOpen] = useState(false);
  const [schemeDetailData, setSchemeDetailData] = useState<any>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [addSchemeOpen, setAddSchemeOpen] = useState(false);
  const [schemeToEdit, setSchemeToEdit] = useState<Scheme | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };

  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
    filters
  });

  // ✅ Fetch schemes only once when tab first opens
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.filters.lender !== filters.lender ||
      prev.filters.partner !== filters.partner;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchSchemesThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
        },
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.lender,
    filters.partner,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);

  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchSchemesThunk());

  }, [token, hasFetched, dispatch]);

  const handleRefresh = () => {
    dispatch(setPage(1));
    dispatch(fetchSchemesThunk());
  };

  const handleRowClick = async (scheme: Scheme) => {
    setSelectedScheme(scheme);
    setSchemeDetailOpen(true);
    setIsLoadingDetail(true);
    try {
      const response = await import('../../services/scheme').then((mod) =>
        mod.fetchSchemeDetails(scheme._id, token)
      );
      setSchemeDetailData(response);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCloseSchemeDetail = () => {
    setSchemeDetailOpen(false);
    setSelectedScheme(null);
    setSchemeDetailData(null);
  };

  const handleAddScheme = () => {
    setSchemeToEdit(null);
    setAddSchemeOpen(true);
  };

  const handleEditScheme = (scheme: Scheme) => {
    setSchemeToEdit(scheme);
    setAddSchemeOpen(true);
  };

  const handleCloseAddScheme = () => {
    setAddSchemeOpen(false);
    setSchemeToEdit(null);
  };

  const handleSchemeSuccess = () => {
    dispatch(fetchSchemesThunk());
  };

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    const processedValue = key === 'partner' ? value.toUpperCase() : value;
    dispatch(setFilters({ ...filters, [key]: processedValue }));
    dispatch(setPage(1));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handelRefresh = () => {
    dispatch(fetchSchemesThunk());
  }

  // Format partners for display
  const formatPartners = (partners: string[]) => {
    if (!partners || partners.length === 0) return <span className="text-gray-400">None</span>;

    if (partners.length === 1 && partners[0] === "") {
      return <span className="text-gray-400">All Partners</span>;
    }

    const displayCount = 2;
    const displayed = partners.slice(0, displayCount);
    const remaining = partners.length > displayCount ? partners.length - displayCount : 0;

    return (
      <div>
        {displayed.map((partner, index) => (
          <div key={index} className="mb-1">
            <span className="px-2 py-0.5 bg-blue-100 rounded-full text-xs text-blue-700 uppercase">
              {partner}
            </span>
          </div>
        ))}
        {remaining > 0 && (
          <span className="text-xs text-gray-500">+{remaining} more</span>
        )}
      </div>
    );
  };

  // Format lenders for display
  const formatLenders = (lenders: string[]) => {
    if (!lenders || lenders.length === 0) return <span className="text-gray-400">None</span>;

    return (
      <div>
        {lenders.map((lender, index) => (
          <div key={index} className="mb-1">
            {renderLenderBadge(lender)}
          </div>
        ))}
      </div>
    );
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender: string) => {
    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Table columns
  const columns: ColumnDef<Scheme>[] = [
    {
      id: 'serialNo',
      header: 'Sr. No',
      cell: ({ row }) => {
        const serialNo = row.original.serialNumber || ((page - 1) * pageSize + row.index + 1);
        return (
          <span className="text-xs font-medium">
            {serialNo}
          </span>
        );
      },
      size: 50
    },
    {
      id: 'name',
      header: 'Scheme Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="flex items-center">
            <CreditCard size={14} className="flex-shrink-0 mr-1 text-indigo-500" />
            <span className="text-sm font-medium">
              {row.original.name}
            </span>
          </div>
          <div className="flex items-center mt-1 text-xs text-gray-500">
            <Calendar size={12} className="flex-shrink-0 mr-1" />
            <span>{row.original.grossTenure} months (Adv. EMI: {row.original.advancedEMI})</span>
          </div>
        </div>
      ),
      size: 180
    },
    {
      id: 'processingRate',
      header: 'Processing Rate',
      accessorKey: 'processingRate',
      cell: ({ row }) => (
        <div className="flex items-center">
          <Percent size={14} className="flex-shrink-0 mr-1 text-green-500" />
          <span className="text-sm">
            {row.original.processingRate}%
          </span>
        </div>
      ),
      size: 120
    },
    {
      id: 'lender',
      header: 'Lenders',
      accessorKey: 'lender',
      cell: ({ row }) => (
        <div className="flex flex-col">
          {formatLenders(row.original.lender)}
        </div>
      ),
      size: 150
    },
    {
      id: 'partners',
      header: 'Partners',
      accessorKey: 'forPartners',
      cell: ({ row }) => (
        <div className="flex flex-col">
          {formatPartners(row.original.forPartners)}
        </div>
      ),
      size: 200
    }
  ];

  return (
    <div className="w-full no-scrollbar">
      <div className="flex justify-between items-center mb-4">
        <Button
          variant="outline"
          onClick={toggleFilters}
          className="flex items-center"
          size="sm"
        >
          <Filter size={14} className="mr-1" />
          Filters
          <ChevronDown size={14} className="ml-1" />
        </Button>

        <Button
          onClick={handleAddScheme}
          variant="primary"
          className="flex items-center bg-indigo-600 hover:bg-indigo-700"
          size="sm"
        >
          <Plus size={14} className="mr-1" />
          Add New Scheme
        </Button>
      </div>

      {/* Filter section */}
      {showFilters && (
        <div className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm no-scrollbar">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Lender
              </label>
              <select
                value={filters.lender || ''}
                onChange={(e) => handleFilterChange('lender', e.target.value)}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Lenders</option>
                <option value="cashe">Cashe</option>
                <option value="zype">Zype</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Partner
              </label>
              <Input
                type="text"
                value={filters.partner || ''}
                onChange={(e) => handleFilterChange('partner', e.target.value)}
                placeholder="e.g., CLEVERPE"
                className="w-full uppercase"
              />
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleResetFilters}
                variant="outline"
                size="sm"
                className="text-gray-600"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={schemes}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          onRefresh={handleRefresh}
          enableRowClick
          onRowClick={handleRowClick}
          searchPlaceholder="Search schemes..."
          emptyStateMessage="No schemes found"
        />
      </div>

      <SchemeDetailModal
        isOpen={schemeDetailOpen}
        onClose={handleCloseSchemeDetail}
        schemeData={schemeDetailData}
        isLoading={isLoadingDetail}
        onEdit={handleEditScheme}
        selectedScheme={selectedScheme}
      />

      <AddSchemeForm
        isOpen={addSchemeOpen}
        onClose={handleCloseAddScheme}
        onSuccess={handleSchemeSuccess}
        schemeToEdit={schemeToEdit}
        token={token}
      />
    </div>
  );
};

export default SchemesPage; 